import { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Image-upload widget with drag & drop + click-to-upload + paste-URL support.
 */
export const ImageUpload = ({ value, onChange, label = "Image" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 8 * 1024 * 1024) return toast.error("Max 8MB");
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = data.path.startsWith("http") ? data.path : `${BACKEND_URL}${data.path}`;
      onChange(fullUrl);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div>
      <label className="label-eyebrow block mb-1">{label}</label>
      <div className="flex gap-2 items-center">
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste URL or drop file →"
          className="flex-1 px-3 py-2 bg-[#F5F2EB] border border-border text-sm focus:outline-none focus:border-foreground"
          data-testid="image-url-input"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-2 px-3 py-2 border border-border bg-white text-sm hover:border-foreground transition-colors duration-200 disabled:opacity-50"
          data-testid="image-upload-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {busy ? "Uploading…" : "Upload"}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="p-2 border border-border bg-white text-muted-foreground hover:text-destructive"
            title="Clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`mt-2 border border-dashed p-4 text-center text-xs cursor-pointer transition-colors duration-200 ${
          dragOver ? "border-[#C95B3B] bg-[#C95B3B]/5 text-foreground" : "border-border text-muted-foreground hover:border-foreground"
        }`}
        data-testid="image-dropzone"
      >
        {busy ? (
          <span className="inline-flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin"/> Uploading…</span>
        ) : dragOver ? (
          "Release to upload"
        ) : (
          "Drop image here or click to browse — JPG · PNG · WEBP · GIF up to 8MB"
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      {value && (
        <div className="mt-2">
          <img src={value} alt="preview" className="w-24 h-24 object-cover border border-border" />
        </div>
      )}
    </div>
  );
};
